import React, { useState, useEffect, useCallback } from 'react';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Activity, Package, ArrowRightLeft, TrendingUp, TrendingDown, Clock,
  RefreshCw, Filter, User, BarChart3, Zap, Truck
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import SupplierComparisonView from './SupplierComparisonView';

const T = {
  bg: '#09090b', paper: '#18181b', subtle: '#27272a',
  primary: '#3b82f6', accent: '#f97316',
  success: '#22c55e', warning: '#eab308', danger: '#ef4444', info: '#0ea5e9',
  border: '#27272a', text: '#fafafa', textMuted: '#a1a1aa', textDim: '#71717a',
};

const typeConfig = {
  in: { color: T.success, label: 'INTRARE', icon: TrendingUp },
  receipt: { color: T.success, label: 'RECEPTIE', icon: Package },
  quick_receipt: { color: T.success, label: 'RECEPTIE RAPIDA', icon: Zap },
  out: { color: T.danger, label: 'IESIRE', icon: TrendingDown },
  transfer: { color: T.info, label: 'TRANSFER', icon: ArrowRightLeft },
  adjustment: { color: T.warning, label: 'AJUSTARE', icon: Activity },
};

const getTypeInfo = (type) => typeConfig[type] || { color: T.textDim, label: type?.toUpperCase() || '?', icon: Activity };

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="rounded-xl border p-4 transition-all hover:shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
    style={{ background: T.paper, borderColor: T.border }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-extrabold font-mono" style={{ color }}>{value}</p>
        <p className="text-[11px]" style={{ color: T.textDim }}>{label}</p>
        {sub && <p className="text-[10px] mt-0.5" style={{ color: T.textDim }}>{sub}</p>}
      </div>
    </div>
  </div>
);

const TimelineItem = ({ mov }) => {
  const info = getTypeInfo(mov.movement_type);
  const Icon = info.icon;
  const timeStr = mov.created_at ? new Date(mov.created_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : '';
  const dateStr = mov.created_at ? new Date(mov.created_at).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' }) : '';

  return (
    <div className="flex gap-3 group" data-testid={`timeline-item-${mov.id?.substring(0, 8)}`}>
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
          style={{ background: `${info.color}20`, border: `2px solid ${info.color}40` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: info.color }} />
        </div>
        <div className="w-px flex-1 min-h-[20px]" style={{ background: T.border }} />
      </div>
      <div className="flex-1 pb-4 group-hover:translate-x-1 transition-transform">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold font-mono px-1.5 py-0.5 rounded" style={{ background: `${info.color}15`, color: info.color }}>{info.label}</span>
              <span className="text-sm font-medium truncate">{mov.article_name || 'Articol'}</span>
              <span className="text-xs font-mono font-bold" style={{ color: T.success }}>{mov.quantity}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-[11px]" style={{ color: T.textDim }}>
              {mov.from_bin_location && <span className="font-mono">{mov.from_bin_location}</span>}
              {mov.from_bin_location && mov.to_bin_location && <ArrowRightLeft className="w-3 h-3 shrink-0" style={{ color: T.textDim }} />}
              {mov.to_bin_location && <span className="font-mono">{mov.to_bin_location}</span>}
              {!mov.from_bin_location && !mov.to_bin_location && mov.from_bin_code && <span className="font-mono">{mov.from_bin_code}</span>}
              {!mov.from_bin_location && !mov.to_bin_location && mov.to_bin_code && <span className="font-mono">{mov.to_bin_code}</span>}
            </div>
            {mov.notes && <p className="text-[10px] mt-0.5 truncate" style={{ color: T.textDim }}>{mov.notes}</p>}
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs font-mono font-bold" style={{ color: T.textMuted }}>{timeStr}</p>
            <p className="text-[10px] font-mono" style={{ color: T.textDim }}>{dateStr}</p>
            {mov.performed_by_name && (
              <div className="flex items-center gap-1 mt-0.5 justify-end">
                <User className="w-2.5 h-2.5" style={{ color: T.textDim }} />
                <span className="text-[10px]" style={{ color: T.textDim }}>{mov.performed_by_name}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const WMSActivityDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [hours, setHours] = useState(24);
  const [activeTab, setActiveTab] = useState('activity');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/wms/activity-dashboard?hours=${hours}`);
      setData(res.data);
    } catch { toast.error('Eroare la incarcarea datelor'); }
    finally { setLoading(false); }
  }, [hours]);

  // Real-time: refresh on stock/ticket changes
  useRealtimeEvent('stock_change', fetchData);
  useRealtimeEvent('new_ticket', fetchData);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredTimeline = data?.timeline?.filter(m =>
    filterType === 'all' || m.movement_type === filterType || (filterType === 'in' && (m.movement_type === 'in' || m.movement_type?.includes('receipt')))
  ) || [];

  const inCount = (data?.by_type?.in || 0) + (data?.by_type?.receipt || 0) + (data?.by_type?.quick_receipt || 0);
  const outCount = data?.by_type?.out || 0;
  const transferCount = data?.by_type?.transfer || 0;

  return (
    <div className="min-h-screen -m-6 -mt-4" style={{ background: T.bg, color: T.text, fontFamily: 'Manrope, sans-serif' }} data-testid="wms-activity-dashboard">
      {/* Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: T.border, background: T.paper }}>
        <div className="flex items-center gap-6">
          <div>
            <h1 className="text-xl font-extrabold tracking-tight flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-400" />
              DASHBOARD WMS
            </h1>
            <p className="text-xs mt-0.5" style={{ color: T.textDim }}>Activitate si analiza furnizori</p>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 bg-zinc-900 rounded-lg p-0.5">
            <button
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${activeTab === 'activity' ? 'bg-blue-600 text-white shadow' : 'text-zinc-400 hover:text-white'}`}
              onClick={() => setActiveTab('activity')} data-testid="tab-activity">
              <Activity className="w-3.5 h-3.5 inline mr-1.5" />Activitate
            </button>
            <button
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${activeTab === 'suppliers' ? 'bg-blue-600 text-white shadow' : 'text-zinc-400 hover:text-white'}`}
              onClick={() => setActiveTab('suppliers')} data-testid="tab-suppliers">
              <Truck className="w-3.5 h-3.5 inline mr-1.5" />Comparativ Furnizori
            </button>
          </div>
        </div>
        {activeTab === 'activity' && (
          <div className="flex items-center gap-2">
            <Select value={String(hours)} onValueChange={v => setHours(parseInt(v))}>
              <SelectTrigger className="w-36 h-8 text-xs bg-zinc-800 border-zinc-700 text-zinc-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">Ultimele 6h</SelectItem>
                <SelectItem value="12">Ultimele 12h</SelectItem>
                <SelectItem value="24">Ultimele 24h</SelectItem>
                <SelectItem value="48">Ultimele 48h</SelectItem>
                <SelectItem value="168">Ultima saptamana</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white h-8" onClick={fetchData} data-testid="btn-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Reincarca
            </Button>
          </div>
        )}
      </div>

      {activeTab === 'suppliers' ? (
        <div className="px-6 py-4">
          <SupplierComparisonView />
        </div>
      ) : loading && !data ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data ? (
        <div className="px-6 py-4 space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-3">
            <StatCard icon={Activity} label="Total Miscari" value={data.total_movements} color={T.primary} sub={`ultimele ${hours}h`} />
            <StatCard icon={TrendingUp} label="Intrari / Receptii" value={inCount} color={T.success} />
            <StatCard icon={TrendingDown} label="Iesiri" value={outCount} color={T.danger} />
            <StatCard icon={ArrowRightLeft} label="Transferuri" value={transferCount} color={T.info} />
          </div>

          {/* Chart + Top Articles/Users */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: T.textMuted }}>
                <BarChart3 className="w-4 h-4 text-blue-400" /> Activitate pe Ore
              </h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.hourly_chart} barGap={1}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="hour" tick={{ fill: T.textDim, fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: T.textDim, fontSize: 10 }} axisLine={false} tickLine={false} width={25} />
                  <Tooltip contentStyle={{ background: T.paper, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 12 }} />
                  <Bar dataKey="in" fill={T.success} radius={[3, 3, 0, 0]} name="Intrari" stackId="a" />
                  <Bar dataKey="out" fill={T.danger} radius={[0, 0, 0, 0]} name="Iesiri" stackId="a" />
                  <Bar dataKey="transfer" fill={T.info} radius={[3, 3, 0, 0]} name="Transferuri" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-4">
              {/* Top Articles */}
              <div className="rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
                <h3 className="text-xs font-bold mb-2" style={{ color: T.textMuted }}>Top Articole Active</h3>
                <div className="space-y-1.5 max-h-[100px] overflow-y-auto">
                  {data.top_articles?.slice(0, 5).map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px]">
                      <span className="truncate flex-1" style={{ color: T.textMuted }}>{a.article_name}</span>
                      <span className="font-mono font-bold ml-2" style={{ color: T.success }}>{a.movements}</span>
                    </div>
                  ))}
                  {(!data.top_articles || data.top_articles.length === 0) && (
                    <p className="text-[11px] text-center py-2" style={{ color: T.textDim }}>Nicio activitate</p>
                  )}
                </div>
              </div>
              {/* Top Users */}
              <div className="rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
                <h3 className="text-xs font-bold mb-2" style={{ color: T.textMuted }}>Utilizatori Activi</h3>
                <div className="space-y-1.5">
                  {data.top_users?.slice(0, 5).map((u, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px]">
                      <div className="flex items-center gap-1.5">
                        <User className="w-3 h-3" style={{ color: T.textDim }} />
                        <span style={{ color: T.textMuted }}>{u.user}</span>
                      </div>
                      <span className="font-mono font-bold" style={{ color: T.primary }}>{u.movements}</span>
                    </div>
                  ))}
                  {(!data.top_users || data.top_users.length === 0) && (
                    <p className="text-[11px] text-center py-2" style={{ color: T.textDim }}>Nicio activitate</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="rounded-xl border overflow-hidden" style={{ background: T.paper, borderColor: T.border }}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: T.border, background: T.subtle }}>
              <h3 className="text-sm font-bold flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-400" /> Timeline Miscari
              </h3>
              <div className="flex items-center gap-1">
                <Filter className="w-3.5 h-3.5 mr-1" style={{ color: T.textDim }} />
                {['all', 'in', 'out', 'transfer'].map(f => (
                  <button key={f} data-testid={`filter-${f}`}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${filterType === f ? 'bg-blue-600 text-white' : 'text-zinc-500 hover:text-white hover:bg-zinc-800'}`}
                    onClick={() => setFilterType(f)}>
                    {f === 'all' ? 'Toate' : f === 'in' ? 'Intrari' : f === 'out' ? 'Iesiri' : 'Transfer'}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-4 max-h-[400px] overflow-y-auto" data-testid="timeline-feed">
              {filteredTimeline.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-20" style={{ color: T.textDim }} />
                  <p className="text-sm" style={{ color: T.textMuted }}>Nicio miscare in perioada selectata</p>
                </div>
              ) : filteredTimeline.map((mov, i) => (
                <TimelineItem key={mov.id || i} mov={mov} />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WMSActivityDashboard;
